import React from 'react';
import classNames from 'classnames';
import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import TextAreaField from 'components/TextAreaField';
import useHandleClickOutside from 'hooks/useHandleClickOutside';
import ClockIcon from 'icons/Clock.icon';
import { MessageOrderEntry } from '../../utils/stageFiles';

import './StageMessageStep.styles.scss';

type StageMessageStepProps = {
  className?: string;
  entry: MessageOrderEntry;
  number: number;
  isOpen: boolean;
  // Sits before the number, e.g. a drag handle.
  handle?: React.ReactNode;
  action: React.ReactNode;
  onToggle: () => void;
  onClose: () => void;
};

const StageMessageStep: React.FC<StageMessageStepProps> = (props) => {
  const {
    className,
    entry,
    number,
    isOpen,
    handle,
    action,
    onToggle,
    onClose,
  } = props;

  const { itemIndex, message, sentWith } = entry;

  const { t } = useTranslation();

  // A click anywhere else collapses it back to the preview.
  const { contentRef } = useHandleClickOutside(() => isOpen && onClose());

  // The field drops an emptied value, so text can be missing.
  const text = message.text?.trim();

  const classes = classNames(
    'stage-message-step',
    { 'stage-message-step--open': isOpen },
    className,
  );

  return (
    <div className={classes} ref={contentRef}>
      <div className="stage-message-step__header">
        {handle}

        <button
          type="button"
          className="stage-message-step__toggle"
          aria-expanded={isOpen}
          onClick={onToggle}
        >
          <span className="stage-message-step__number">{number}</span>

          <span className="stage-message-step__info">
            <span className="stage-message-step__title">{t('message')}</span>
            <span className="stage-message-step__meta">
              {sentWith
                ? t('sentWithItem', { name: sentWith.name })
                : t('sentAtTheEndOfOrder')}
            </span>
            {!isOpen && (
              <span
                className={classNames('stage-message-step__preview', {
                  'stage-message-step__preview--empty': !text,
                })}
              >
                {text || t('emptyMessageNotSaved')}
              </span>
            )}
          </span>
        </button>

        <div className="stage-message-step__action" data-no-drag>
          {action}
        </div>
      </div>

      {isOpen && (
        <>
          <Field
            name={`orderItems[${itemIndex}].text`}
            component={TextAreaField}
            placeholder={t('writeAMessage')}
            autoResize
            autoFocus
          />

          <p className="stage-message-step__hint">
            <ClockIcon />
            {t('messageStepHint')}
          </p>
        </>
      )}
    </div>
  );
};

export default StageMessageStep;
